import { forwardRef, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Button, IconButton, Input, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { type IconName, Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';

import { PRESENT_VALUE_SENTINEL } from '../../../types/QueryBuilder';
import type { FieldOptions, FilterToken, Operator, ParsedToken } from '../../../types/QueryBuilder';
import { AutocompletePopover } from '../AutocompletePopover';
import { useQueryBuilderContext } from '../context';
import { useFilterBarContext } from '../FilterBarContext';
import { useAutocompleteNavigation } from '../hooks/useAutocompleteNavigation';
import { useQueryBuilderGridItem } from '../hooks/useQueryBuilderGridItem';
import {
  dedupeOptionsByValue,
  expressionTypeAtOffset,
  fieldOptionsForScope,
  getDefaultOperator,
  getOperatorDisplay,
  inferFieldType,
  isOperatorCompatible,
  matchScore,
  OPERATOR_OPTIONS,
  PRESENT_COMPATIBLE_OPERATORS,
  scopeForExpressionType,
} from '../utils';

import styles from './LozengeFilter.module.css';

/** Sentinel for the pinned "Search for value:" option in the value autocomplete. */
const SEARCH_VALUE_SENTINEL = '__SEARCH_VALUE__';

/**
 * Case-insensitive substring match of an autocomplete option against the typed text.
 * Mirrors the field-key autocomplete in FreeTextToken so suggestions are *filtered*
 * as the user types (not just highlighted). Matches on the raw value and on the
 * human-readable displayName. Used by both the key and value cells.
 */
function optionMatchesSearch(
  option: { value: string | number | boolean; displayName?: string },
  search: string,
): boolean {
  const query = search.toLowerCase();
  if (String(option.value).toLowerCase().includes(query)) {
    return true;
  }
  return (option.displayName ?? '').toLowerCase().includes(query);
}

/** Splits a comma-separated multi-value string into individual trimmed values. */
function parseMultiValue(str: string): string[] {
  return str
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

/**
 * Returns the comma-delimited segment that the cursor currently sits within.
 * For example, given "foo, bar|, baz" (| = cursor at offset 8), returns "bar".
 */
function getSegmentAtCursor(text: string, cursorPos: number): string {
  const before = text.slice(0, cursorPos);
  const segStart = before.lastIndexOf(',') + 1;
  const afterCursor = text.slice(cursorPos);
  const commaAfter = afterCursor.indexOf(',');
  const segEnd = commaAfter === -1 ? text.length : cursorPos + commaAfter;
  return text.slice(segStart, segEnd).trim();
}

/**
 * Keeps an <input>'s width flush with its rendered text via a hidden mirror element.
 *
 * A single div is appended to document.body (position:fixed, invisible) with the
 * input's font properties copied once. After every render and on every native input
 * event, the mirror is filled with input.value || input.placeholder, measured, and
 * the result — plus the input's own padding and border — is written to input.style.width.
 *
 * useLayoutEffect (no dep array) fires synchronously after every DOM commit, so the
 * input is already the correct width on the first visible frame. The native listener
 * covers keystrokes that arrive between React renders. Both are cleaned up per render
 * so the listener always points to the live element.
 */
function useInputWidthMirror(inputRef: React.RefObject<HTMLInputElement | null>) {
  const mirrorRef = useRef<HTMLDivElement | null>(null);

  // Create the mirror once; remove it on unmount.
  useLayoutEffect(() => {
    const mirror = document.createElement('div');
    mirror.style.cssText =
      'position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;white-space:pre;z-index:-1';
    document.body.appendChild(mirror);
    mirrorRef.current = mirror;
    return () => {
      document.body.removeChild(mirror);
      mirrorRef.current = null;
    };
  }, []);

  // Measure + attach native listener after every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const input = inputRef.current;
    const mirror = mirrorRef.current;
    if (!input || !mirror) {
      return;
    }

    // Copy font properties from the live input once — font changes post-load are not a concern.
    if (!mirror.dataset.fontCopied) {
      const cs = getComputedStyle(input);
      mirror.style.fontFamily = cs.fontFamily;
      mirror.style.fontSize = cs.fontSize;
      mirror.style.fontWeight = cs.fontWeight;
      mirror.style.letterSpacing = cs.letterSpacing;
      mirror.dataset.fontCopied = '1';
    }

    // Capture narrowed non-null refs so the inner function doesn't require assertions.
    const inputEl = input;
    const mirrorEl = mirror;

    function measure() {
      const cs = getComputedStyle(inputEl);
      mirrorEl.textContent = inputEl.value || inputEl.placeholder || '';
      const extra =
        parseFloat(cs.paddingLeft) +
        parseFloat(cs.paddingRight) +
        parseFloat(cs.borderLeftWidth) +
        parseFloat(cs.borderRightWidth);
      inputEl.style.width = `${mirrorEl.offsetWidth + extra}px`;
    }

    measure();
    inputEl.addEventListener('input', measure);
    return () => inputEl.removeEventListener('input', measure);
  });
}

// True when the user's last pointerdown landed outside both the value input's filter bar (its
// nearest [role="grid"]) and the popover listbox — i.e. a genuine click-away. A null target (no
// pointer interaction: keyboard nav, or a purely programmatic focus move) counts as "inside" so
// react-aria's own focus restoration is still treated as a steal. When there's no resolvable
// filter-bar boundary we can't be sure, so we fall back to "inside" (preserve steal behavior).
function pointerLandedOutsideCombobox(
  target: EventTarget | null,
  input: HTMLInputElement,
  listbox: HTMLElement | null,
): boolean {
  if (!(target instanceof Node)) {
    return false;
  }
  if (listbox?.contains(target)) {
    return false;
  }
  const grid = input.closest('[role="grid"]');
  if (!grid) {
    return false;
  }
  return !grid.contains(target);
}

// A focus target that means react-aria stole focus from the open value popover rather than
// the user leaving: one of the popover's own options, or nothing/body (react-aria restoring
// the closing FreeText popover lands on the body since its trigger is aria-hidden).
//
// A null/body target is ambiguous — react-aria's restoration lands there too — so we
// disambiguate with the user's most recent pointerdown: if they just pointed at a non-focusable
// area *outside* the combobox and its popover, that's a real dismissal (return false so the cell
// deactivates), not a steal.
function isValueFocusSteal(
  el: EventTarget | null,
  listboxId: string,
  input: HTMLInputElement,
  lastPointerDownTarget: EventTarget | null,
): boolean {
  const container = document.getElementById(listboxId);
  if (container && el instanceof Node && container.contains(el)) {
    return true;
  }
  if (el === null || el === document.body) {
    return !pointerLandedOutsideCombobox(lastPointerDownTarget, input, container);
  }
  return false;
}

const EditableCell = forwardRef<
  HTMLSpanElement,
  {
    onClick: () => void;
    onDeactivate: () => void;
    onInput: React.FormEventHandler<HTMLInputElement>;
    value: string;
    /** Overrides the button label without affecting the input value. */
    displayValue?: string;
    /** Overrides the input's initial value without affecting the button label or placeholder. */
    initialInputValue?: string;
    disabled?: boolean;
    isActivePrev: boolean;
    isActive: boolean;
    isActiveNext: boolean;
    /** When true, never switches to an input — the button stays rendered with a focused style when active. */
    readOnly?: boolean;
    showQuotes?: boolean;
    /**
     * Static text rendered immediately before the input/button — used to surface fixed
     * delimiters (e.g. the `/` slashes wrapping a regex value) as visible context that
     * sits outside the editable text.
     */
    prefix?: string;
    /** Static text rendered immediately after the input/button — see `prefix`. */
    suffix?: string;
    /**
     * Icon rendered after the label in button/disabled mode — used as a dropdown
     * indicator (e.g. the chevron on the "exists" value cell) signaling that
     * clicking the cell opens a list to pick from. Not rendered in edit mode.
     */
    trailingIcon?: IconName;
    /**
     * Optional Launchpad Tooltip body shown on hover/focus. Mirrors the LogicParen
     * type-cell tooltip pattern; supply pre-formatted ReactNode (e.g. a bold first
     * line followed by a description) for richer content.
     */
    tooltip?: React.ReactNode;
    /**
     * When true, the editable input only accepts characters that can form a valid
     * number — digits, a single decimal point, and a leading sign. Non-numeric
     * keystrokes (and pasted non-numeric text) are dropped via `onBeforeInput`.
     * The input also surfaces `inputMode="decimal"` for mobile keyboards.
     */
    numberOnly?: boolean;
    'data-part'?: string;
    'aria-label'?: string;
    /** When set, the input renders as a combobox pointing at this listbox id so react-aria's Autocomplete gives the popover virtual focus (DOM focus stays in the input). */
    comboboxListboxId?: string;
    /** Whether the combobox popover is currently open (drives aria-expanded). */
    comboboxExpanded?: boolean;
  }
>(
  (
    {
      onClick,
      onDeactivate,
      onInput,
      value,
      displayValue,
      initialInputValue,
      disabled,
      isActivePrev,
      isActive,
      isActiveNext,
      readOnly,
      showQuotes,
      prefix,
      suffix,
      trailingIcon,
      tooltip,
      numberOnly,
      'data-part': dataPart,
      'aria-label': ariaLabel,
      comboboxListboxId,
      comboboxExpanded,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useInputWidthMirror(inputRef);

    // Records the user's most recent pointerdown target so a null/body focus loss can be
    // classified as a react-aria steal vs. a real click-away (see isValueFocusSteal).
    const lastPointerDownRef = useRef<EventTarget | null>(null);

    // Single owner of "while the value popover is open, DOM focus stays in the input". A
    // capture-phase focusin runs synchronously after react-aria moves focus, so whenever it
    // lands on one of our options (async suggestions loading) or on the body (react-aria
    // restoring the closing FreeText popover), we send it straight back — no timing race.
    // A parallel pointerdown listener lets us tell that body focus apart from the user clicking
    // a non-focusable area outside the filter bar (a dismissal), which we must NOT fight.
    useEffect(() => {
      if (!comboboxListboxId || readOnly || !isActive) {
        return undefined;
      }
      const onPointerDown = (e: PointerEvent) => {
        lastPointerDownRef.current = e.target;
      };
      const onFocusIn = (e: FocusEvent) => {
        const input = inputRef.current;
        if (!input || !input.isConnected || e.target === input) {
          return;
        }
        if (isValueFocusSteal(e.target, comboboxListboxId, input, lastPointerDownRef.current)) {
          input.focus();
        }
      };
      document.addEventListener('pointerdown', onPointerDown, true);
      document.addEventListener('focusin', onFocusIn, true);
      return () => {
        document.removeEventListener('pointerdown', onPointerDown, true);
        document.removeEventListener('focusin', onFocusIn, true);
      };
    }, [comboboxListboxId, readOnly, isActive]);

    // Affixes only render in button/disabled mode — they're appended inline to the
    // visible label so they look like part of the value text. In edit mode the input
    // is shown alone so the user types just the inner content (e.g. the regex pattern
    // without its `/` delimiters).
    const isInputMode = !readOnly && isActive;
    const buttonLabelText = displayValue ?? (showQuotes && value === '' ? '""' : value);
    const labelWithAffixes = `${prefix ?? ''}${buttonLabelText}${suffix ?? ''}`;

    const cellElement = isInputMode ? (
      <Input
        ref={inputRef}
        data-part={dataPart}
        placeholder={value}
        {...(comboboxListboxId
          ? {
              // Declare the value input as a combobox so react-aria's Autocomplete keeps DOM
              // focus here and gives the option list virtual focus (the async suggestions can't steal it).
              role: 'combobox' as const,
              'aria-expanded': comboboxExpanded,
              'aria-controls': comboboxListboxId,
              'aria-autocomplete': 'list' as const,
            }
          : {})}
        {...(numberOnly ? { inputMode: 'decimal' as const } : {})}
        onBeforeInput={
          numberOnly
            ? (e: React.FormEvent<HTMLInputElement> & { data?: string | null; nativeEvent: InputEvent }) => {
                // Only allow digits, a single decimal point, and a leading sign.
                // `e.nativeEvent.data` is the inserted text (null for deletions, which
                // we never block). For paste / drag-and-drop the whole pasted string
                // arrives at once so we validate the entire chunk, not character-by-
                // character. The full-value regex on the projected new value catches
                // cases like a second `.` or a `-` that isn't at the start.
                const inserted = e.nativeEvent.data ?? null;
                if (inserted === null) {
                  return;
                }
                const input = e.currentTarget;
                const start = input.selectionStart ?? input.value.length;
                const end = input.selectionEnd ?? input.value.length;
                const next = `${input.value.slice(0, start)}${inserted}${input.value.slice(end)}`;
                if (!/^-?\d*\.?\d*$/.test(next)) {
                  e.preventDefault();
                }
              }
            : undefined
        }
        onInput={onInput}
        onBlur={(e) => {
          // A blur into our own option or a programmatic focus move to nowhere is react-aria
          // stealing focus, not the user leaving — keep the cell open; the focusin owner above
          // restores focus to the input. But a null/body blur that the user provoked by clicking
          // a non-focusable area outside the filter bar is a real dismissal, so we deactivate.
          if (
            comboboxListboxId &&
            isValueFocusSteal(e.relatedTarget, comboboxListboxId, e.currentTarget, lastPointerDownRef.current)
          ) {
            return;
          }
          onDeactivate();
        }}
        defaultValue={initialInputValue ?? value}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const container = inputRef.current?.parentElement;
            onDeactivate();
            requestAnimationFrame(() => {
              container?.querySelector<HTMLElement>('button')?.focus();
            });
            return;
          }
          // Escape: mirror the operator cell's behavior. The container-level handler in
          // useAutocompleteNavigation can race with the popover's own focus restoration
          // (the trigger ref points at an aria-hidden span, so restoration falls back to
          // body), leaving the cell button unfocused. Handling Escape locally here lets us
          // synchronously stop propagation and refocus the cell's button via RAF after the
          // input re-renders back into a button.
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            const container = inputRef.current?.parentElement;
            onDeactivate();
            requestAnimationFrame(() => {
              container?.querySelector<HTMLElement>('button')?.focus();
            });
          }
        }}
      />
    ) : disabled ? (
      <span aria-label={ariaLabel} className={clsx(styles.cell, styles.cellDisabled)} data-part={dataPart}>
        {labelWithAffixes}
        {trailingIcon && <Icon name={trailingIcon} size="small" className={styles.trailingIcon} aria-hidden="true" />}
      </span>
    ) : (
      <Button
        aria-label={ariaLabel}
        className={clsx(styles.cell, {
          [styles.cellFocusedPrev]: isActivePrev,
          [styles.cellFocused]: isActive,
          [styles.cellFocusedNext]: isActiveNext,
        })}
        data-part={dataPart}
        onBlur={isActive ? onDeactivate : undefined}
        onClick={() => {
          if (!disabled) {
            onClick();
          }
        }}
      >
        <span>
          {labelWithAffixes}
          {trailingIcon && <Icon name={trailingIcon} size="small" className={styles.trailingIcon} aria-hidden="true" />}
        </span>
      </Button>
    );

    return (
      <span className={styles.editableCell}>
        <span ref={ref} aria-hidden="true" className={styles.editableCellOverlay} />
        {tooltip ? (
          <TooltipTrigger delay={500}>
            {cellElement}
            <Tooltip>{tooltip}</Tooltip>
          </TooltipTrigger>
        ) : (
          cellElement
        )}
      </span>
    );
  },
);

EditableCell.displayName = 'EditableCell';

// Re-export from types.ts for backward compatibility.
export type { FieldOptions };

interface LozengeFilterProps {
  /** Stable identifier used to track which lozenge has the open popover. */
  tokenKey: string;
  value: FilterToken;
  onChange: (value: FilterToken) => void;
  onRemove: () => void;
  removable?: boolean;
  icon?: IconName;
  /** Sample values per field, used to populate autocomplete for the value cell. */
  options?: FieldOptions[];
  /** When provided, key/op/value selection dispatches structured actions to the QueryBuilder context. */
  parsedToken?: Extract<ParsedToken, { type: 'FILTER' }>;
}

export function LozengeFilter({
  tokenKey,
  value,
  onChange,
  onRemove,
  removable,
  icon,
  options,
  parsedToken,
}: LozengeFilterProps) {
  const { activePopover, setActivePopover, dispatch, wrapperRef, parsedQuery } = useQueryBuilderContext();
  const { readonly, gridState, showTypeLabels, relationalFieldOptions } = useFilterBarContext();
  const filterDisplayValue =
    'values' in value ? (value.values?.join(',') ?? '') : 'value' in value ? String(value.value ?? '') : '';
  const { containerProps, cellProps } = useQueryBuilderGridItem(
    tokenKey,
    gridState,
    `${value.field}:${value.operator}:${filterDisplayValue}`,
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const keyRef = useRef<HTMLSpanElement>(null);
  const opRef = useRef<HTMLSpanElement>(null);
  const valueRef = useRef<HTMLSpanElement>(null);

  // Values that have appeared in the multi-value input during the current edit session.
  // Seeded when the value cell opens; accumulated as options are selected.
  // Persists even when values are deselected so options remain visible in the autocomplete.
  const [multiSessionOptions, setMultiSessionOptions] = useState<string[]>([]);

  // Tracks the segment of the value input that the cursor is currently within, for
  // cursor-aware autocomplete filtering. Null until the user first interacts with the input.
  const [cursorSegment, setCursorSegment] = useState<string | null>(null);
  // Whether the user has actually edited the value text since opening the popover. Used to
  // decide when to *filter* the value suggestions (vs. only sort/highlight). Reset on open so
  // that merely focusing, clicking, or arrow-keying through a committed value doesn't collapse
  // the list to the committed substring.
  const [hasEditedValue, setHasEditedValue] = useState(false);
  // Same idea for the key cell: `value.field` doubles as the cell's live text, so on a fresh
  // open it holds the committed field name and filtering by it would collapse the list to the
  // current field. Only filter once the user has actually retyped the key.
  const [hasEditedKey, setHasEditedKey] = useState(false);

  // Resolve field metadata within this lozenge's scope first (so a same-named field
  // in another scope can't supply the wrong type), then fall back to any scope.
  const fieldData = useMemo(() => {
    const all = options ?? [];
    const scope = scopeForExpressionType(
      parsedToken ? expressionTypeAtOffset(parsedQuery, parsedToken.location.start.offset) : undefined,
      relationalFieldOptions,
    );
    return (
      fieldOptionsForScope(all, scope).find((o) => o.field === value.field) ?? all.find((o) => o.field === value.field)
    );
  }, [options, value.field, parsedToken, parsedQuery, relationalFieldOptions]);

  // The field-edit dropdown must offer only fields valid in this lozenge's scope (e.g. session
  // fields inside any_session), matching fieldData above and the FreeTextToken creation flow.
  const scopedOptions = useMemo(() => {
    const scope = scopeForExpressionType(
      parsedToken ? expressionTypeAtOffset(parsedQuery, parsedToken.location.start.offset) : undefined,
      relationalFieldOptions,
    );
    return fieldOptionsForScope(options ?? [], scope);
  }, [options, parsedToken, parsedQuery, relationalFieldOptions]);

  // Guard by readonly: activePopover lives in QueryBuilderContext and is shared
  // across every FilterBar in the tree. Without this gate, a readonly lozenge
  // would render its own popover whenever an editable lozenge with the same
  // tokenKey opens one — visible whenever multiple FilterBars render the same
  // parsedQuery (e.g. an editable + readonly pair).
  const isThisActive = !readonly && activePopover?.tokenKey === tokenKey;
  const activePart = isThisActive ? activePopover.part : null;
  // Regex operators (// and !//) take a single regex literal, never a multi-value list: a regex
  // already expresses "any of" via alternation (a|b), its body may legally contain the comma the
  // multiselect path splits on (e.g. /\d{1,3}/), and the backend forwards it as one value. So they
  // opt out of multiselect and render a single-literal preview popover (see the popover layout below).
  const isRegexValueOp = value.operator === '//' || value.operator === '!//';
  // Boolean fields only ever take a single value (true/false) — the one hard "never multiselect"
  // gate. Detected from the explicit `valueType`, falling back to inferring from sample values.
  let isBooleanField = false;
  if (fieldData?.valueType) {
    isBooleanField = fieldData.valueType === 'boolean';
  } else if (fieldData?.sampleValues && fieldData.sampleValues.length > 0) {
    isBooleanField = inferFieldType(fieldData.sampleValues) === 'BOOLEAN';
  }
  // Number fields restrict the input to digits / decimal point / leading sign, and the popover's
  // "Search for value:" pin reads "Search for number:" instead. Detection is purely from the
  // explicit `valueType` (inferFieldType doesn't return NUMBER).
  const isNumberField = fieldData?.valueType === 'number';
  // Numeric comparison operators (>, >=, <, <=) commit a single value even on number fields — the
  // metrics backend lists them in singleValueOperators / SINGLE_VALUE_OPS and 400s on multiple values.
  const isComparisonOp =
    value.operator === '>' || value.operator === '>=' || value.operator === '<' || value.operator === '<=';
  // The synthetic `context` scope field (Option A) picks one kind at a time.
  const isContextScopeField = fieldData?.field === 'context';
  // Multiselect is FIELD-driven and requires known field metadata: an unconfigured field (no
  // FieldOptions entry, so its cardinality is unknown) stays single-select rather than silently
  // comma-splitting typed values. Among configured fields: booleans never, the `context` scope field
  // never, numeric comparison ops never, and single-literal regex ops (// , !//) never; every other
  // configured field (number/string, regardless of the legacy `multi` flag) does. This replaces the
  // old operator-gated multi/supportsMultipleValues requirement.
  const fieldAllowsMultiselect =
    !!fieldData && !isBooleanField && !isContextScopeField && !isComparisonOp && !isRegexValueOp;
  // Whether the field can hold multiple values at once (multi-valued/list attribute). When true,
  // chosen values combine with AND; falsy (the default) means OR. No field sets this yet, so AND is
  // dormant and OR is the live path — see the value-commit branch in handleSelect.
  const fieldHoldsMultiple = !!fieldData?.supportsMultipleValues;
  const isMultiSelect = activePart === 'value' && fieldAllowsMultiselect;

  // For multiselect fields, a comma-containing sample value breaks the comma-joined input, so drop
  // it from the selectable options. Single-select fields keep commas fine and pass through.
  const valueSampleValues = useMemo(() => {
    const samples = fieldData?.sampleValues ?? [];
    return fieldAllowsMultiselect ? samples.filter((s) => !String(s.value).includes(',')) : samples;
  }, [fieldData?.sampleValues, fieldAllowsMultiselect]);

  const spanRefs = { key: keyRef, op: opRef, value: valueRef } as const;
  const activeTriggerRef = (
    activePart && activePart !== 'type' ? spanRefs[activePart] : keyRef
  ) as React.RefObject<HTMLElement | null>;

  function handleCellPress(part: 'key' | 'op' | 'value') {
    if (readonly) {
      return;
    }
    if (isThisActive && activePopover?.part === part) {
      setActivePopover(null);
    } else {
      if (part === 'key') {
        setHasEditedKey(false);
      }
      // Value-popover session state (cursor segment, hasEditedValue, multiSessionOptions)
      // is seeded by the open-transition effect below, which covers every open path.
      setActivePopover({ tokenKey, part });
    }
  }

  function handleCellInput(part: 'key' | 'op' | 'value', text: string) {
    if (parsedToken) {
      if (part === 'key') {
        if (!isValidKey(text)) {
          return;
        }
        setHasEditedKey(true);
        dispatch({ type: 'UPDATE_FILTER_KEY', token: parsedToken, key: text });
      } else if (part === 'op') {
        dispatch({ type: 'UPDATE_FILTER_OP', token: parsedToken, op: text as Operator });
      } else if (part === 'value') {
        dispatch({ type: 'UPDATE_FILTER_VALUE', token: parsedToken, value: text });
      }
    } else {
      if (part === 'key') {
        if (!isValidKey(text)) {
          return;
        }
        setHasEditedKey(true);
        const normalizedKey = text;
        if ('values' in value) {
          onChange({ field: normalizedKey, operator: value.operator, values: value.values });
        } else {
          onChange({
            field: normalizedKey,
            operator: value.operator,
            value: 'value' in value ? value.value : undefined,
          });
        }
      } else if (part === 'op') {
        const newOp = text as Operator;
        const v = 'values' in value ? value.values?.join(',') : 'value' in value ? value.value : undefined;
        onChange({ field: value.field, operator: newOp, value: v });
      } else if (part === 'value') {
        if ('values' in value) {
          onChange({ field: value.field, operator: value.operator, values: [text] });
        } else {
          onChange({ field: value.field, operator: value.operator, value: text });
        }
      }
    }
  }

  // When a popover opens externally (e.g. via setActivePopover from FreeTextToken),
  // the op cell is readOnly so it stays a Button with no autoFocus, and the value cell
  // can lose its autoFocus to react-aria's focus restoration from the closing FreeText
  // popover. Explicitly focus the active part on activation, then again on the next
  // frame to override any restoration that lands after our autoFocus.
  useLayoutEffect(() => {
    if (activePart !== 'op' && activePart !== 'value') {
      return undefined;
    }
    const findEl = () => containerRef.current?.querySelector<HTMLElement>(`[data-part="${activePart}"]`);
    findEl()?.focus();
    const id = requestAnimationFrame(() => findEl()?.focus());
    return () => cancelAnimationFrame(id);
  }, [activePart]);

  // Track cursor position in the value input so autocomplete can filter by the
  // segment the cursor is currently within (not just the last segment).
  useEffect(() => {
    if (activePart !== 'value') {
      return;
    }
    const input = containerRef.current?.querySelector<HTMLInputElement>('[data-part="value"]');
    if (!input) {
      return;
    }

    const el = input;
    function updateFromCursor() {
      const pos = el.selectionStart ?? el.value.length;
      setCursorSegment(getSegmentAtCursor(el.value, pos));
    }

    // keyup: catches arrow-key cursor movement (no text change, so onInput won't fire)
    // click: catches mouse cursor placement
    el.addEventListener('keyup', updateFromCursor);
    el.addEventListener('click', updateFromCursor);
    return () => {
      el.removeEventListener('keyup', updateFromCursor);
      el.removeEventListener('click', updateFromCursor);
    };
  }, [activePart]);

  /**
   * Commits the cursor segment as a value when the user picks the pinned
   * "Search for value:" option. For single-select the typed text is already
   * mirrored to state by onInput, so we only need to close the popover. For
   * multi-select the segment must be added to the values list and to
   * multiSessionOptions so it persists as a popover option after deselection.
   */
  // Commit a value (scalar or array) to the token: dispatch to the reducer in the parsedToken
  // path, else call onChange for standalone usage. Arrays flow through unchanged so stringifyToken
  // emits the unquoted comma form (field:=:a,b) that re-parses back into an array.
  function commitFilterValue(newValue: string | Array<string | number | boolean>) {
    if (parsedToken) {
      dispatch({ type: 'UPDATE_FILTER_VALUE', token: parsedToken, value: newValue });
    } else {
      onChange({ field: value.field, operator: value.operator, value: newValue });
    }
  }

  function commitSearchValue() {
    if (!isMultiSelect) {
      setActivePopover(null);
      return;
    }
    const segment = valueSearchText;
    if (!segment) {
      return;
    }
    const currentValues = concreteSelectedValues;
    const newValues = currentValues.includes(segment) ? currentValues : [...currentValues, segment];
    setMultiSessionOptions((prev) => (prev.includes(segment) ? prev : [...prev, segment]));
    commitFilterValue(newValues);
    syncValueInput(newValues);
  }

  /**
   * Sync the uncontrolled value input to `newValues` and move cursor to end.
   * A trailing ", " is appended in the DOM only — onInput's parseMultiValue
   * strips it back out before storing, so the trailing separator never reaches
   * the query state but lets the user immediately type the next value.
   */
  function syncValueInput(newValues: string[]) {
    // Match only an <input> — a `multi`, non-`allowCustom` field (e.g. a read-only multiselect)
    // renders its value cell as a <button>, which has no value/setSelectionRange to sync.
    const input = containerRef.current?.querySelector<HTMLInputElement>('input[data-part="value"]');
    if (!input) {
      return;
    }
    const formatted = newValues.length > 0 ? `${newValues.join(', ')}, ` : '';
    input.value = formatted;
    input.setSelectionRange(formatted.length, formatted.length);
    // Programmatic value/selection updates don't reliably scroll an input's view to follow
    // the caret — even when the input is focused. After the next paint (so the mirror-driven
    // width adjustment in useInputWidthMirror has run), pin the horizontal scroll to the
    // right edge so the just-moved caret is visible without needing a keystroke to nudge it.
    requestAnimationFrame(() => {
      input.scrollLeft = input.scrollWidth;
    });
  }

  function handleSelect(selected: string | number | boolean) {
    if (activePart === 'value' && selected === SEARCH_VALUE_SENTINEL) {
      commitSearchValue();
      return;
    }

    // "present" is a toggle: selecting it while already active clears the value; otherwise applies sentinel.
    if (activePart === 'value' && selected === PRESENT_VALUE_SENTINEL) {
      const newValue = displayValue === PRESENT_VALUE_SENTINEL ? '' : PRESENT_VALUE_SENTINEL;
      if (parsedToken) {
        dispatch({ type: 'UPDATE_FILTER_VALUE', token: parsedToken, value: newValue });
      } else if ('values' in value) {
        onChange({ field: value.field, operator: value.operator, values: [newValue] });
      } else {
        onChange({ field: value.field, operator: value.operator, value: newValue });
      }
      if (!isMultiSelect) {
        setActivePopover(null);
        // Match the focus restoration done by the Escape handler so focus lands on the
        // value cell button instead of being scattered by the popover's default close behavior.
        requestAnimationFrame(() => {
          containerRef.current?.querySelector<HTMLElement>('button[data-part="value"]')?.focus();
        });
      }
      return;
    }

    if (parsedToken) {
      // Dispatch structured actions so the reducer updates the raw query string
      if (activePart === 'op') {
        const newOp = selected as Operator;
        // The reducer clears the `__present__` sentinel inline when switching
        // to a non-present-compatible op (numeric comparisons, regex,
        // starts with/ends with); we just need to hop focus to the value cell
        // here so the user can supply a concrete value.
        const clearingPresent = displayValue === PRESENT_VALUE_SENTINEL && !PRESENT_COMPATIBLE_OPERATORS.has(newOp);
        dispatch({ type: 'UPDATE_FILTER_OP', token: parsedToken, op: newOp });
        if (clearingPresent || displayValue === '') {
          setActivePopover({ tokenKey, part: 'value' });
          return;
        }
      } else if (activePart === 'value') {
        if (isMultiSelect) {
          const currentValues = concreteSelectedValues;
          const selectedStr = String(selected);
          const newValues = currentValues.includes(selectedStr)
            ? currentValues.filter((v) => v !== selectedStr)
            : [...currentValues, selectedStr];
          setMultiSessionOptions((prev) => (prev.includes(selectedStr) ? prev : [...prev, selectedStr]));
          // Live (OR) path: dispatch the ARRAY so stringifyToken emits field:=:a,b, which the
          // converters treat as OR. When fieldHoldsMultiple the values would instead be AND-joined
          // as separate tokens (which the converters already AND) — dormant until a field opts in.
          commitFilterValue(newValues);
          syncValueInput(newValues);
          return; // keep popover open
        }
        // Finalizing a single value closes the popover; keep focus on the trailing
        // free-text cell so the user can chain another filter.
        dispatch({
          type: 'UPDATE_FILTER_VALUE',
          token: parsedToken,
          value: String(selected),
          focusOverride: { itemKey: 'end' },
        });
        // The popover restores focus after it closes, so wait two frames before
        // moving the cursor back to the bar.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const inputs = wrapperRef.current?.querySelectorAll<HTMLInputElement>('input[data-key^="ft-"]');
            inputs?.[inputs.length - 1]?.focus();
          });
        });
      } else if (activePart === 'key') {
        const newField = String(selected);
        // Resolve from the same scoped list the key picker offered (scopedOptions), not the
        // merged full list — a same-named field in another scope would supply the wrong type.
        const newFieldOptions = scopedOptions.find((o) => o.field === newField);
        let newOp: Operator | undefined;
        if (newFieldOptions) {
          if (newFieldOptions.valueType === 'string') {
            newOp = '=';
          } else {
            const fieldType = inferFieldType(newFieldOptions.sampleValues);
            if (!isOperatorCompatible(value.operator, fieldType)) {
              newOp = getDefaultOperator(fieldType);
            }
          }
        }
        dispatch({ type: 'UPDATE_FILTER_KEY', token: parsedToken, key: newField, newOp });
      }
    } else {
      // Fallback: call onChange with the updated FilterToken (for standalone / hardcoded usage)
      if (activePart === 'op') {
        const newOp = selected as Operator;
        const isClearingPresent = displayValue === PRESENT_VALUE_SENTINEL && !PRESENT_COMPATIBLE_OPERATORS.has(newOp);
        const v = isClearingPresent
          ? ''
          : 'values' in value
            ? value.values?.join(',')
            : 'value' in value
              ? value.value
              : undefined;
        onChange({ field: value.field, operator: newOp, value: v });
        // Clearing the present sentinel (or starting from an already-empty
        // value) means we have nothing meaningful to filter on yet — hop
        // focus to the value cell so the user can supply one.
        if (isClearingPresent || displayValue === '') {
          setActivePopover({ tokenKey, part: 'value' });
          return;
        }
      } else if (activePart === 'value') {
        if (isMultiSelect) {
          const currentValues = concreteSelectedValues;
          const selectedStr = String(selected);
          const newValues = currentValues.includes(selectedStr)
            ? currentValues.filter((v) => v !== selectedStr)
            : [...currentValues, selectedStr];
          setMultiSessionOptions((prev) => (prev.includes(selectedStr) ? prev : [...prev, selectedStr]));
          onChange({ field: value.field, operator: value.operator, value: newValues });
          syncValueInput(newValues);
          return;
        }
        if ('values' in value) {
          onChange({ field: value.field, operator: value.operator, values: [String(selected)] });
        } else {
          onChange({ field: value.field, operator: value.operator, value: selected });
        }
      } else if (activePart === 'key') {
        if ('values' in value) {
          onChange({ field: String(selected), operator: value.operator, values: value.values });
        } else if ('value' in value) {
          onChange({ field: String(selected), operator: value.operator, value: value.value });
        }
      }
    }
    setActivePopover(null);
  }

  const rawDisplayValue =
    'values' in value
      ? (value.values?.join(',') ?? '')
      : Array.isArray(value.value)
        ? value.value.map(String).join(',')
        : 'value' in value
          ? String(value.value ?? '')
          : '';
  const displayValue = rawDisplayValue === '""' ? '' : rawDisplayValue;

  // The committed values as a structured array — the source of truth for selection state
  // (checkmarks, toggles, labels, counts). Read straight off the token so a value containing a comma
  // isn't split into phantom selections the way re-parsing the comma-joined displayValue would.
  const committedValues = useMemo<string[]>(() => {
    const raw =
      'values' in value
        ? (value.values ?? [])
        : Array.isArray(value.value)
          ? value.value
          : 'value' in value && value.value !== null && value.value !== undefined
            ? [value.value]
            : [];
    return raw.map(String).filter((v) => v !== '');
  }, [value]);

  // Number of committed values, used to pick the "… one of" operator-label suffix. A regex op is
  // always one literal (its body may contain commas), so force 1; otherwise count committedValues.
  const committedValueCount = isRegexValueOp ? 1 : committedValues.length;

  // Seed the value-popover session state whenever the value popover transitions to open,
  // regardless of which path opened it: a cell press, an operator change hopping focus to
  // the value cell, a key selection auto-opening it, or an external open from FreeTextToken.
  // Without this, a prior value-typing session leaves `hasEditedValue` set and a stale
  // `cursorSegment`, so filtering kicks in immediately and hides most sample values in the
  // newly opened list. useLayoutEffect so the re-seeded state flushes before paint.
  const wasValuePopoverOpen = useRef(false);
  useLayoutEffect(() => {
    const isOpen = activePart === 'value';
    if (isOpen && !wasValuePopoverOpen.current) {
      // Seed cursor segment to '' for present mode (sentinel) and for multiselect fields with
      // existing values so valueSearchText starts empty (show all options).
      setCursorSegment(displayValue === PRESENT_VALUE_SENTINEL || (fieldAllowsMultiselect && displayValue) ? '' : null);
      setHasEditedValue(false);
      if (fieldAllowsMultiselect) {
        // Don't seed the sentinel into multiSessionOptions — it's not a real value and
        // would create a duplicate option with no displayName in the popover.
        setMultiSessionOptions(displayValue === PRESENT_VALUE_SENTINEL ? [] : committedValues);
      }
    }
    wasValuePopoverOpen.current = isOpen;
  }, [activePart, displayValue, fieldAllowsMultiselect, committedValues]);

  // Non-custom mode: cells render as read-only buttons that only accept
  // autocomplete selections, and cell labels use the matching `displayValue`
  // from FieldOptions (key) or sampleValues (value) instead of the raw text.
  //
  // When `fieldData` is undefined the field is unknown to this FilterBar — the
  // user is either mid-typing a custom key or has just committed a brand-new
  // lozenge from FreeTextToken's "Search for field:" option with a custom
  // field name. Both cases need free-text input on the value cell; otherwise
  // the value cell mounts as a read-only button the user cannot type into.
  // Treat unknown fields as custom for both cells.
  const allowCustomKey = fieldData ? fieldData.allowCustom === true : true;
  // Boolean fields get the operator-cell treatment — only `true` / `false` are
  // valid, so the value cell stays a read-only button that commits a sample
  // value via the popover. Free typing here would let the user enter anything.
  const allowCustomValue = isBooleanField ? false : fieldData ? fieldData.allowCustom === true : true;

  // Look up a sample value's display label by its raw value. Used in non-custom
  // mode to translate the stored value into the human-readable label.
  const sampleDisplayLabel = (raw: string): string =>
    fieldData?.sampleValues?.find((s) => String(s.value) === raw)?.displayValue ?? raw;

  // For multi-select fields, the committed value cell button shows only the first
  // selection plus a count of additional ones (e.g. "USA, +2 more") rather than
  // the full comma-separated list.
  const multiValueParts = fieldAllowsMultiselect ? committedValues : null;
  const multiValueLabels =
    multiValueParts && !allowCustomValue ? multiValueParts.map(sampleDisplayLabel) : multiValueParts;
  const multiValueButtonLabel =
    multiValueLabels && multiValueLabels.length > 1
      ? `${multiValueLabels[0]} +${multiValueLabels.length - 1} more`
      : undefined;

  // True when the present ("exists") sentinel is the current value and the value cell is
  // open. The popover shows the "exists" row checked; picking a sample value replaces it.
  const isPresentModeActive = activePart === 'value' && displayValue === PRESENT_VALUE_SENTINEL;

  // The value the Autocomplete wrapper reports as the field's text. It must mirror the *whole*
  // value input, not just the cursor segment: a multiselect field's input holds the full
  // comma-joined list, so feeding only the active segment here would misrepresent the field value
  // and (should react-aria ever control the input) drop the other committed values on re-render.
  // Cursor-segment-aware option *filtering* is unaffected — it flows separately via valueSearchText
  // into AutocompletePopover's searchText. Present mode reports empty (the input is blank); regex
  // is excluded (its single-literal preview must not be filtered/overwritten by the typed text).
  const valueCellInputText = useMemo(() => {
    if (activePart !== 'value' || isRegexValueOp || displayValue === PRESENT_VALUE_SENTINEL) {
      return '';
    }
    return fieldAllowsMultiselect ? (multiValueParts?.join(', ') ?? '') : displayValue;
  }, [activePart, isRegexValueOp, displayValue, fieldAllowsMultiselect, multiValueParts]);

  // The text used to filter autocomplete options: for the value cell, this is the
  // segment at the current cursor position rather than the last comma segment.
  const valueSearchText = useMemo(() => {
    if (activePart !== 'value') {
      return '';
    }
    // If the user has interacted with the input, use the cursor-aware segment.
    if (cursorSegment !== null) {
      return cursorSegment;
    }
    // Fallback before first interaction: use the last segment (mirrors prior behavior).
    return fieldAllowsMultiselect ? (displayValue.split(',').at(-1)?.trim() ?? '') : displayValue;
  }, [activePart, cursorSegment, displayValue, fieldAllowsMultiselect]);

  // Only *filter* the value suggestions once the user has actually edited the value text.
  // On a fresh open (or when the user merely clicks / arrow-keys through an existing value),
  // a single-value field's valueSearchText falls back to the whole committed value, and
  // filtering by it would hide every other option until the value is cleared — so until the
  // text is edited we show all options (matching the multi-value cell, which seeds an empty
  // segment on open). Editing is tracked separately from cursor movement because keyup/click
  // also set cursorSegment without changing the text.
  const isTypingValue = hasEditedValue;

  // Derive the type-appropriate options for the active part (unfiltered — Autocomplete handles text filtering).
  const rawPopoverOptions = useMemo(() => {
    if (activePart === 'op') {
      let allowedOps = OPERATOR_OPTIONS;
      if (fieldData?.allowedOperators) {
        allowedOps = OPERATOR_OPTIONS.filter((o) => fieldData.allowedOperators?.includes(o.value));
      } else if (fieldData?.valueType === 'string') {
        allowedOps = OPERATOR_OPTIONS.filter((o) => o.value === '=' || o.value === '!=');
      }
      return allowedOps;
    }

    if (activePart === 'value') {
      // Regex operators (// and !//) accept a single regex literal — short-circuit the usual
      // sample/present/search options and surface only one selectable entry that previews
      // the regex about to be committed. Below it, render every sample value as a non-
      // selectable preview row so the user can see which existing values match the pattern
      // they're typing — matched rows get a green check via `trailingIconName`.
      //
      // Selecting the lead entry routes through SEARCH_VALUE_SENTINEL so the typed text
      // (already mirrored to state via onInput) is committed and the popover closes.
      if (value.operator === '//' || value.operator === '!//') {
        const isMatch = value.operator === '//';
        const regex = displayValue;
        const stripped = regex.length >= 2 && regex.startsWith('/') && regex.endsWith('/') ? regex.slice(1, -1) : regex;
        // Build the runtime regex once — invalid patterns silently produce a `null` matcher
        // so the preview rows render without checks rather than throwing.
        let matcher: RegExp | null = null;
        if (stripped) {
          try {
            matcher = new RegExp(stripped);
          } catch {
            matcher = null;
          }
        }
        // For the positive-match operator (`//`) a regex match means the row WOULD be
        // included → green check. For the negative-match operator (`!//`) a regex match
        // means the row WOULD be excluded → red minus-circle.
        const matchIconName = isMatch ? ('check-circle' as const) : ('minus-circle' as const);
        const matchIconTone = isMatch ? ('success' as const) : ('danger' as const);
        // Build matched and unmatched preview rows separately, then concatenate so all
        // matches surface at the top of the Preview section while unmatched rows keep
        // the original sample-value order.
        const matchedPreview: Array<{
          value: string;
          displayName: string;
          category: string;
          disabled: boolean;
          trailingIconName?: typeof matchIconName;
          trailingIconTone?: typeof matchIconTone;
        }> = [];
        const unmatchedPreview: typeof matchedPreview = [];
        for (const { value: v, displayValue: dv } of fieldData?.sampleValues ?? []) {
          const stringValue = String(v);
          const matches = matcher ? matcher.test(stringValue) : false;
          const row = {
            value: stringValue,
            displayName: dv ?? stringValue,
            category: 'Preview',
            disabled: true,
            ...(matches ? { trailingIconName: matchIconName, trailingIconTone: matchIconTone } : {}),
          };
          (matches ? matchedPreview : unmatchedPreview).push(row);
        }
        const previewOptions = [...matchedPreview, ...unmatchedPreview];
        const leadPrefix = `${isMatch ? 'Matches' : 'Does not match'} regex: `;
        return [
          {
            value: SEARCH_VALUE_SENTINEL,
            // Plain text for accessibility / search highlighting; labelNode renders the
            // regex wrapped in <code> so it visually reads as a literal pattern.
            displayName: `${leadPrefix}${regex}`,
            labelNode: (
              <>
                {leadPrefix}
                <code>{regex}</code>
              </>
            ),
            description: `Results which are matched by this regex will be ${isMatch ? 'included' : 'excluded'}`,
          },
          ...previewOptions,
        ];
      }

      const isPresentCompatible = PRESENT_COMPATIBLE_OPERATORS.has(value.operator);
      const presentOption = isPresentCompatible
        ? [{ value: PRESENT_VALUE_SENTINEL, displayName: 'exists', description: 'Matches all possible values' }]
        : [];

      const isInPresentMode = displayValue === PRESENT_VALUE_SENTINEL;

      // Pinned at the top when the cursor segment is non-empty — lets the user commit
      // arbitrary text as the value even when no sample option matches. Wrap the typed
      // value in double quotes when the field is a string type so the user can visually
      // tell the boundary (especially for values containing whitespace or delimiters).
      // Number fields swap the noun to "number" since the input only accepts digits.
      let isStringType = false;
      if (fieldData?.valueType) {
        isStringType = fieldData.valueType === 'string';
      } else if (fieldData?.sampleValues && fieldData.sampleValues.length > 0) {
        isStringType = inferFieldType(fieldData.sampleValues) === 'STRING';
      }
      const searchValueDisplay = isNumberField
        ? `Search for number: ${valueSearchText}`
        : isStringType
          ? `Search for value: "${valueSearchText}"`
          : `Search for value: ${valueSearchText}`;
      // The pinned entry's whole purpose is to commit the *typed* value when no
      // sample option matches — so it only makes sense when the value cell is
      // actually an editable input. Boolean fields (and any other field with
      // `allowCustom: false`) render the value cell as a read-only Button, so
      // the option has nothing to commit; suppress it.
      const searchValueOption =
        valueSearchText && !isInPresentMode && allowCustomValue
          ? [{ value: SEARCH_VALUE_SENTINEL, displayName: searchValueDisplay }]
          : [];

      const valueOptions = valueSampleValues.map(({ value: v, approxCount, displayValue: dv }) => ({
        value: v,
        displayName: dv,
        resultsCount: approxCount,
      }));

      if (fieldAllowsMultiselect) {
        // Include values from the session that aren't in sampleValues so deselected options
        // remain available in the autocomplete for reapplication.
        const sampleSet = new Set(valueOptions.map((o) => String(o.value)));
        const sessionOnlyOpts = multiSessionOptions
          // Filter the sentinel: it's always rendered via presentOption and must not appear
          // as a session option with no displayName, which would create a duplicate key.
          .filter((v) => !sampleSet.has(v) && v !== PRESENT_VALUE_SENTINEL)
          .map((v) => ({
            value: v,
            displayName: undefined as string | undefined,
            resultsCount: undefined as number | undefined,
          }));
        const allOptions = [...valueOptions, ...sessionOnlyOpts];
        // Pin selected options to the top of the list (just below the fixed
        // "exists" row) so they remain visible while the user types and
        // additional selections; unselected options keep their relative order.
        const selectedSet = new Set(committedValues);
        const partitionSelected = <T extends { value: string | number | boolean }>(opts: T[]): T[] => {
          const selected: T[] = [];
          const unselected: T[] = [];
          for (const opt of opts) {
            (selectedSet.has(String(opt.value)) ? selected : unselected).push(opt);
          }
          return [...selected, ...unselected];
        };
        // Filter to the segment at the current cursor position, then sort. Already-selected
        // values are always kept so the user doesn't lose sight of their multi-selection while
        // narrowing the list; matching unselected values follow.
        if (valueSearchText) {
          const narrowed = isTypingValue
            ? allOptions.filter((o) => selectedSet.has(String(o.value)) || optionMatchesSearch(o, valueSearchText))
            : allOptions.slice();
          const sorted = narrowed.sort(
            (a, b) =>
              matchScore(a.displayName ?? String(a.value), valueSearchText) -
              matchScore(b.displayName ?? String(b.value), valueSearchText),
          );
          return [...searchValueOption, ...presentOption, ...partitionSelected(sorted)];
        }
        return [...searchValueOption, ...presentOption, ...partitionSelected(allOptions)];
      }

      if (valueSearchText) {
        // Single-value fields (boolean / context scope / comparison ops) land here. Keep the
        // committed value visible while narrowing so it isn't hidden by the typed filter.
        const selectedSet = new Set(committedValues);
        const narrowed = isTypingValue
          ? valueOptions.filter((o) => selectedSet.has(String(o.value)) || optionMatchesSearch(o, valueSearchText))
          : valueOptions.slice();
        const sorted = narrowed.sort(
          (a, b) =>
            matchScore(a.displayName ?? String(a.value), valueSearchText) -
            matchScore(b.displayName ?? String(b.value), valueSearchText),
        );
        return [...searchValueOption, ...presentOption, ...sorted];
      }
      return [...searchValueOption, ...presentOption, ...valueOptions];
    }

    if (activePart === 'key') {
      const keyOptions = scopedOptions.map((o) => {
        // Prefer the explicit valueType; fall back to inferring from sample values
        // (currently distinguishes BOOLEAN from STRING). The chip is the bare type only —
        // we deliberately don't tag multi fields with `[]` per the design. Suppressed
        // entirely when the FilterBar opts out via `showTypeLabels={false}`.
        let typeLabel: string | undefined;
        if (showTypeLabels) {
          if (o.valueType) {
            typeLabel = o.valueType;
          } else if (o.sampleValues && o.sampleValues.length > 0) {
            typeLabel = inferFieldType(o.sampleValues) === 'BOOLEAN' ? 'boolean' : 'string';
          }
        }
        return {
          value: o.field,
          displayName: o.displayValue,
          iconName: o.iconName,
          description: o.description,
          typeLabel,
        };
      });
      if (value.field) {
        // Filter to the typed text, matching the creation flow in FreeTextToken — but only
        // once the user has retyped the key. `value.field` is also the cell's live text, so
        // on a fresh open it holds the committed field name and filtering by it would hide
        // every other field; until then, sort only.
        const narrowed = hasEditedKey
          ? keyOptions.filter((o) => optionMatchesSearch(o, value.field))
          : keyOptions.slice();
        return narrowed
          .sort(
            (a, b) =>
              matchScore(a.displayName ?? String(a.value), value.field) -
              matchScore(b.displayName ?? String(b.value), value.field),
          )
          .slice(0, 10);
      }
      return keyOptions.slice(0, 10);
    }

    return [];
  }, [
    activePart,
    allowCustomValue,
    committedValues,
    displayValue,
    fieldAllowsMultiselect,
    fieldData,
    hasEditedKey,
    hasEditedValue,
    isTypingValue,
    multiSessionOptions,
    scopedOptions,
    showTypeLabels,
    value.field,
    value.operator,
    valueSampleValues,
    valueSearchText,
  ]);

  // Dedupe once so the exact array the popover renders also drives keyboard
  // navigation (optionCount / handleAutocompleteSelect index). Some branches above
  // can surface the same value twice (e.g. a session-only value equal to a sample
  // value); without a shared deduped list the highlighted row and the committed
  // option would diverge.
  const popoverOptions = useMemo(() => dedupeOptionsByValue(rawPopoverOptions), [rawPopoverOptions]);

  const selectedValues = useMemo(() => {
    if (activePart !== 'value') {
      return [];
    }
    if (fieldAllowsMultiselect) {
      return committedValues;
    }
    if ('values' in value) {
      return (value.values ?? []).map(String);
    }
    if (Array.isArray(value.value)) {
      return value.value.map(String);
    }
    if ('value' in value && value.value !== null && value.value !== undefined) {
      return [String(value.value)];
    }
    return [];
  }, [activePart, committedValues, fieldAllowsMultiselect, value]);

  // Base list for multiselect commits. In "present" (__present__) mode selectedValues holds only
  // the sentinel, but a concrete value must REPLACE present rather than coexist with it — a filter
  // can't serialize as both "exists" and "== x". Stripping the sentinel here means appending or
  // toggling a real value drops present instead of producing a corrupt `field:=:__present__,x`.
  const concreteSelectedValues = useMemo(
    () => selectedValues.filter((v) => v !== PRESENT_VALUE_SENTINEL),
    [selectedValues],
  );

  // When present ("exists") mode is active, only the sentinel renders checked; sample
  // values stay unchecked and selectable so the user can narrow from "exists" straight
  // to specific values (handleSelect replaces the sentinel with the picked value).
  // Clicking the checked "exists" row fires onSelectionChange as a deselect, which the
  // toggled-key logic routes to handleSelect just like an add event.
  const presentModeSelectedKeys = useMemo(
    () => (isPresentModeActive ? [PRESENT_VALUE_SENTINEL] : []),
    [isPresentModeActive],
  );

  function focusNextFreeText() {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const root = container.closest<HTMLElement>('[role="grid"]') ?? wrapperRef.current;
    if (!root) {
      return;
    }
    // FreeText inputs have no data-part; lozenge cell inputs have data-part="key|op|value"
    const freeTextInputs = Array.from(root.querySelectorAll<HTMLInputElement>('input:not([data-part])'));
    const nextInput = freeTextInputs.find(
      // eslint-disable-next-line no-bitwise
      (el) => !!(container.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING),
    );
    nextInput?.focus();
  }

  function handleRemove() {
    // Capture the preceding FreeText input while the container is still mounted,
    // then focus it after the deletion re-render completes.
    const container = containerRef.current;
    if (container) {
      const root = container.closest<HTMLElement>('[role="grid"]') ?? wrapperRef.current;
      if (root) {
        const freeTextInputs = Array.from(root.querySelectorAll<HTMLInputElement>('input:not([data-part])'));
        const precedingInput = [...freeTextInputs].reverse().find(
          // eslint-disable-next-line no-bitwise
          (el) => !!(container.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING),
        );
        if (precedingInput) {
          // Mark the next focus on this input as "side-effect of a remove" so
          // FreeTextToken's onFocus doesn't auto-open its autocomplete popover.
          // A subsequent user-initiated click (mousedown) will still open it.
          precedingInput.dataset.suppressAutocompleteOnFocus = '1';
          requestAnimationFrame(() => precedingInput.focus());
        }
      }
    }
    onRemove();
  }

  function handleAutocompleteSelect(index: number) {
    const option = popoverOptions[index];
    if (!option || ('disabled' in option && option.disabled)) {
      return;
    }
    const capturedPart = activePart;
    handleSelect(option.value);
    if (capturedPart === 'key') {
      // Open the value cell immediately after key selection
      setActivePopover({ tokenKey, part: 'value' });
    } else if (capturedPart === 'value' && !isMultiSelect) {
      // Move focus to the FreeText input that follows this lozenge
      focusNextFreeText();
    }
  }

  // Stable id linking the value combobox input to its popover listbox (aria-controls).
  const valueListboxId = useId();

  const { focusedIndex, containerDataProps, handleKeyDown } = useAutocompleteNavigation({
    optionCount: popoverOptions.length,
    isOpen: isThisActive,
    onSelect: handleAutocompleteSelect,
    // FreeTextToken sets `autoFocusFirst` when JIT-creating a lozenge from a
    // custom field name — the user has already shown intent, so the just-opened
    // popover seeds index 0 to let Enter commit immediately without an ArrowDown.
    defaultFocusedIndex: activePopover?.autoFocusFirst ? 0 : undefined,
    onClose: () => {
      const part = activePart;
      setActivePopover(null);
      if (part) {
        requestAnimationFrame(() => {
          const btn = containerRef.current?.querySelector<HTMLElement>(`button[data-part="${part}"]`);
          // Hop focus through the lozenge's tabIndex=-1 outer container before
          // returning it to the button. The hop produces a blur → focus pair on
          // the button, which resets the wrapping TooltipTrigger's "dismissed
          // for this focus session" flag (read-only cells never unmount, so a
          // direct re-focus alone would keep the tooltip suppressed). Routing
          // through the container — which sits inside the FilterBar wrapper —
          // keeps `:focus-within` true throughout, so the FilterBar outline
          // doesn't flicker the way `btn.blur()` (focus → body) would.
          containerRef.current?.focus();
          requestAnimationFrame(() => btn?.focus());
        });
      }
    },
  });

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- wrapper exists only to stop click propagation; interactive controls live inside
    <div
      ref={containerRef}
      className={styles.container}
      // tabIndex=-1 lets us programmatically focus this element during the
      // Escape close path (see `useAutocompleteNavigation.onClose` below) so
      // we can briefly hop focus off the trigger button without the FilterBar
      // wrapper losing its `:focus-within` outline.
      tabIndex={-1}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Wrapping the cell input and the popover's ListBox under one react-aria Autocomplete gives the */}
      {/* ListBox virtual focus, so async-loaded value suggestions no longer pull DOM focus off the input. */}
      {/* disableAutoFocusFirst + no `filter` keep react-aria's nav/filtering inert; useAutocompleteNavigation */}
      {/* and the app-side option filtering stay authoritative. */}
      <Autocomplete
        inputValue={activePart === 'key' ? value.field : valueCellInputText}
        onInputChange={() => {}}
        disableAutoFocusFirst
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- role="row" is provided via containerProps spread */}
        <div
          {...containerProps}
          {...containerDataProps}
          onKeyDown={isThisActive ? handleKeyDown : undefined}
          onKeyDownCapture={(e) => {
            // Run the existing capture-phase grid-navigation handler from containerProps.
            containerProps.onKeyDownCapture?.(e);
            // Intercept Enter before React Aria's Button can stop propagation.
            // This ensures selecting a highlighted autocomplete option always works,
            // regardless of which cell type (input vs. read-only button) has focus.
            if (isThisActive && e.key === 'Enter' && focusedIndex >= 0) {
              e.preventDefault();
              e.stopPropagation();
              handleAutocompleteSelect(focusedIndex);
            }
          }}
          className={styles.lozengeFilter}
        >
          {icon && (
            <div {...cellProps}>
              <Icon name={icon} size="small" className={styles.icon} />
            </div>
          )}

          {/* Key cell */}
          <div {...cellProps}>
            <EditableCell
              ref={keyRef}
              data-part="key"
              aria-label={`Edit key for filter: ${value.field}`}
              value={value.field}
              displayValue={!allowCustomKey ? (fieldData?.displayValue ?? value.field) : undefined}
              tooltip="Edit field"
              isActivePrev={activePart === 'op'}
              isActive={activePart === 'key'}
              isActiveNext={false}
              readOnly={!allowCustomKey}
              showQuotes
              onClick={() => handleCellPress('key')}
              onDeactivate={() => setActivePopover(null)}
              onInput={(e) => handleCellInput('key', e.currentTarget.value)}
              disabled={readonly}
            />
          </div>

          {/* Operator cell — hidden in the present ("exists") state, where the value
              cell renders the combined "exists ▾" control instead; the is/is-not menu
              returns once a concrete value is set. */}
          {displayValue !== PRESENT_VALUE_SENTINEL && (
            <div {...cellProps}>
              <EditableCell
                ref={opRef}
                data-part="op"
                aria-label={`Edit operator for filter: ${value.field}`}
                value={value.operator}
                displayValue={getOperatorDisplay(value.operator, {
                  // Only multiselect-capable fields (never booleans, the context scope field, the
                  // single-value comparison ops, or single-literal regex ops) read "… one of"; the rest
                  // stay single-value labels.
                  oneOf: fieldAllowsMultiselect && !fieldHoldsMultiple && committedValueCount > 1,
                })}
                tooltip="Edit operator"
                isActivePrev={activePart === 'value'}
                isActive={activePart === 'op'}
                isActiveNext={activePart === 'key'}
                readOnly
                onClick={() => handleCellPress('op')}
                onDeactivate={() => setActivePopover(null)}
                onInput={(e) => handleCellInput('op', e.currentTarget.value)}
                disabled={readonly}
              />
            </div>
          )}

          {/* Value cell */}
          <div {...cellProps}>
            {(() => {
              // Regex tokens are stored with literal `/` delimiters (e.g. `/^foo/`). The
              // delimiters belong to the syntax, not the regex content the user is editing —
              // so strip them for the editable input/button display and surface them as fixed
              // affixes on either side via EditableCell's `prefix`/`suffix` props. The onInput
              // handler re-wraps the typed content with `/` before dispatching so the stored
              // token shape stays consistent.
              const isRegexOp = value.operator === '//' || value.operator === '!//';
              const stripSlashes = (s: string): string =>
                s.length >= 2 && s.startsWith('/') && s.endsWith('/') ? s.slice(1, -1) : s;
              // In the present state the operator cell is hidden and the value cell is
              // the whole phrase: "exists ▾" (or "does not exist ▾"). The trailing
              // chevron signals that specific values can be picked from the list; the
              // label doubles as the input placeholder while the cell is being edited.
              const isPresentValue = displayValue === PRESENT_VALUE_SENTINEL;
              const presentLabel = value.operator === '!=' ? 'does not exist' : 'exists';
              const cellValue = isPresentValue
                ? presentLabel
                : isRegexOp
                  ? stripSlashes(displayValue)
                  : multiValueParts
                    ? multiValueParts.join(', ')
                    : displayValue;
              const cellInitialInputValue = isPresentValue
                ? ''
                : isRegexOp
                  ? // Same value the (placeholder-driven) input would otherwise read; passed
                    // explicitly so an existing `/.../` value lands as just its inner contents.
                    stripSlashes(displayValue)
                  : // Regex operators take a single regex literal — skip the trailing ", " seed
                    // so focusing the cell lands the cursor at the end of the existing pattern
                    // instead of after a comma the user would have to delete.
                    fieldAllowsMultiselect && displayValue
                    ? `${multiValueParts?.join(', ') ?? displayValue}, `
                    : undefined;
              const cellDisplayValue = isPresentValue
                ? presentLabel
                : isRegexOp
                  ? stripSlashes(displayValue)
                  : multiValueButtonLabel
                    ? multiValueButtonLabel
                    : !allowCustomValue && displayValue
                      ? sampleDisplayLabel(displayValue)
                      : undefined;
              return (
                <EditableCell
                  ref={valueRef}
                  data-part="value"
                  aria-label={`Edit value for filter: ${value.field}`}
                  value={cellValue}
                  initialInputValue={cellInitialInputValue}
                  displayValue={cellDisplayValue}
                  tooltip="Edit value"
                  prefix={isRegexOp ? '/' : undefined}
                  suffix={isRegexOp ? '/' : undefined}
                  trailingIcon={isPresentValue ? 'chevron-down' : undefined}
                  isActivePrev={false}
                  isActive={activePart === 'value'}
                  isActiveNext={activePart === 'op'}
                  readOnly={!allowCustomValue}
                  numberOnly={isNumberField && !isRegexOp}
                  comboboxListboxId={valueListboxId}
                  comboboxExpanded={isThisActive && activePart === 'value'}
                  showQuotes
                  onClick={() => handleCellPress('value')}
                  onDeactivate={() => setActivePopover(null)}
                  onInput={(e) => {
                    const el = e.currentTarget;
                    const text = el.value;
                    // Update cursor segment so autocomplete filters by what's at the cursor.
                    const pos = el.selectionStart ?? text.length;
                    setCursorSegment(getSegmentAtCursor(text, pos));
                    setHasEditedValue(true);
                    if (isRegexOp) {
                      // Re-wrap the typed regex content in the literal `/` delimiters before
                      // dispatching, so the stored value keeps its slash-wrapped shape.
                      handleCellInput('value', `/${text}/`);
                    } else if (fieldAllowsMultiselect) {
                      // Commit the parsed values as an ARRAY (not a comma-joined string) so the
                      // round-trip through the query string preserves individual values. Do NOT
                      // update multiSessionOptions here — partial words typed by the user should not
                      // become persistent options; only explicit autocomplete selections are tracked.
                      commitFilterValue(parseMultiValue(text));
                    } else {
                      handleCellInput('value', text);
                    }
                  }}
                  disabled={readonly}
                />
              );
            })()}
          </div>

          {removable && (
            <div {...cellProps}>
              <TooltipTrigger delay={500}>
                <IconButton
                  className={styles.removeButton}
                  icon="cancel"
                  variant="minimal"
                  size="small"
                  aria-label={`Remove filter: ${value.field}`}
                  onPress={handleRemove}
                />
                <Tooltip>Remove filter</Tooltip>
              </TooltipTrigger>
            </div>
          )}
        </div>

        {isThisActive && (
          <AutocompletePopover
            id={valueListboxId}
            triggerRef={activeTriggerRef}
            isOpen
            onOpenChange={(open) => !open && setActivePopover(null)}
            onSelect={handleSelect}
            options={popoverOptions}
            // When the value cell is hosting a regex op, the popover renders the lead
            // selectable option in an unnamed section above a "Preview" section
            // containing one disabled row per sample value (matches get a green check).
            groupByCategory={activePart === 'value' && isRegexValueOp}
            // Interactive multiselect is FIELD-gated via isMultiSelect (never a boolean, the context
            // scope field, or a comparison op). Present mode also renders a multi list but is
            // non-interactive — handleSelect early-returns on non-sentinel clicks there.
            multiSelect={isMultiSelect || isPresentModeActive}
            selectedKeys={isPresentModeActive ? presentModeSelectedKeys : isMultiSelect ? selectedValues : undefined}
            focusedIndex={focusedIndex}
            searchText={
              activePart === 'key'
                ? value.field
                : // The regex value popover renders a fixed lead row plus a
                  // preview section of every sample value — it should not be
                  // filtered, sorted, or highlighted by what the user is typing.
                  activePart === 'value' && !isRegexValueOp
                  ? valueSearchText
                  : undefined
            }
          />
        )}
      </Autocomplete>
    </div>
  );
}

LozengeFilter.displayName = 'LozengeFilter';

/**
 * Returns true when the key text is safe to dispatch.
 *
 * Keys with spaces are allowed — stringifyToken will auto-quote them.
 * Bare (unquoted) keys must still satisfy the grammar's identifier rule so
 * that a trailing period or other invalid char doesn't produce an unparseable
 * query string.  Empty keys are always rejected.
 */
function isValidKey(text: string): boolean {
  if (!text) {
    return false;
  }
  if (/\s/.test(text)) {
    return true;
  } // will be auto-quoted by stringifyToken
  return /^[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)*$/.test(text);
}
